import type { DateDisplayProps } from '@app/src/components/DateDisplay'
import DateDisplay, { useGetFormattedDate } from '@app/src/components/DateDisplay'
import TruncatingText from '@app/src/components/Ui/TruncatingText'
import { BoltOutlined } from '@mui/icons-material'
import type { TypographyProps } from '@mui/material'
import { Box, Tooltip, Typography } from '@mui/material'
import React from 'react'
import ParentCellOrFragment from './ParentCellOrFragment'

type ValueCellProps = {
  value?: DateDisplayProps['value']
  withTime?: DateDisplayProps['withTime']
  disableCell?: boolean
  disabled?: boolean
  /**
   * Optional automation tooltip title.
   * If provided, displays bolt icon and wraps content in tooltip.
   * IMPORTANT: Compute this at parent level to avoid performance issues.
   */
  automationTooltip?: string
} & TypographyProps

const DateCell: React.FC<ValueCellProps> = ({
  value,
  disableCell,
  withTime,
  disabled,
  automationTooltip,
  ...props
}) => {
  const { formatValueToDate } = useGetFormattedDate()
  const dateContent = (
    <Typography variant="body1" color={disabled ? 'textSecondary' : 'textPrimary'} {...props}>
      {value ? <DateDisplay value={value} withTime={withTime} /> : '-'}
    </Typography>
  )

  // If automation tooltip provided, wrap with bolt icon and tooltip
  if (automationTooltip) {
    const content = (
      <Box display="flex" alignItems="center" gap={0.5}>
        <BoltOutlined color={disabled ? 'disabled' : 'action'} />
        {dateContent}
      </Box>
    )

    return (
      <ParentCellOrFragment disableCell={disableCell}>
        <Tooltip title={automationTooltip}>
          <Box component="span" display="inline-flex">
            {content}
          </Box>
        </Tooltip>
      </ParentCellOrFragment>
    )
  }

  // Regular date cell: truncate to a single line and show the full date in a tooltip only when clipped.
  return (
    <ParentCellOrFragment disableCell={disableCell}>
      <TruncatingText
        text={value ? formatValueToDate({ value, withTime }) : '-'}
        variant="body1"
        color={disabled ? 'textSecondary' : 'textPrimary'}
        {...props}
      >
        {value ? <DateDisplay value={value} withTime={withTime} /> : '-'}
      </TruncatingText>
    </ParentCellOrFragment>
  )
}

export default DateCell
